import { allowedCardTypes, PathMap } from '@config/index';
import { CommonApiService } from '@services/common-api-service';
import { loginController, loginResponse } from '@services/login-controller';
import { MoengageInstance } from '@services/moengage-service';
import { Component, Element, EventEmitter, h, Host, JSX, Prop, State, Event as StencilEvent, Watch } from '@stencil/core';
import { disclaimerStore } from '@store/disclaimer-store';
import { pathStore } from '@store/path-store';
import { sharedStore } from '@store/sharedStore';
import { If } from '@utils/If';
import { ConfigService } from '../../../services/config-service';
import { Disclaimer, NavigationMenu, PartnerDisclaimerData } from '../types';
import { cardTypeLogos } from './cardTypeLogos';

@Component({
  tag: 'nav-header',
  styleUrl: 'nav-header.scss',
  shadow: true
})
export class NavHeader {
  @Prop() isLoggedin: boolean = false;
  @Element() host: HTMLElement;
  @Prop() assetPath: string;
  @Prop() headerTransparent: boolean = false;
  @Prop() position: 'absolute' | 'fixed' | 'sticky' = 'fixed';
  @Prop() loginEventName: string = 'show-smartbuy-login';
  @Prop() checkLoginEventName: string = 'check-login';
  @Prop() addCardEventName: string = 'show-add-card-without-session';
  @Prop() selectedCard: any = {};
  @Prop() activeProgram: string = '';
  @Prop() selectedCardName: string = '';
  @Prop() selectedCardPoints: string = '';
  @Prop() selectedCardLastFourDigits: string = '';
  @Prop() firstName: string = '';
  @Prop() cardsAvailable: any = [];
  @Prop() customerInfo: any = {};
  @Prop() menuItems: NavigationMenu;
  @Prop() redirectDomainUrl: string = '';
  @Prop() allowedCardTypes: string[] = allowedCardTypes;
  @Prop() switchEnabled: boolean = true;
  @Prop() showCardSwitcher: boolean = true;
  @Prop() wishList: { enabled: boolean; count: number } = { enabled: false, count: 0 };
  @Prop() cartFlow: { enabled: boolean; count: number } = { enabled: false, count: 0 };
  @Prop() customLogout: boolean = false;
  @Prop() showSearchIcon: boolean = false;
  @Prop() visibleOnMobile: boolean = true;
  @Prop() displayHeader: boolean = true;
  @Prop() enableCookieConsent: boolean = true;
  @Prop() platformPopupEnabled: boolean = true;
  @Prop() enableSessionHandler: boolean = true;
  @Prop() activeMenuItem: string = '';
  @Prop() isMain: boolean = false;
  @Prop() ssrEnabled: boolean = false;
  @Prop() bankDomainUrl: string = '';
  @Prop() showBackButton: boolean = false;
  @Prop() isHomePage: boolean = false;
  @Prop() domainUrl: {
    mainDomain: {
      url: string;
      basePath: string;
    };
    subDomain: {
      url: string;
      basePath: string;
    };
  } = {
      mainDomain: {
        url: '',
        basePath: ''
      },
      subDomain: {
        url: '',
        basePath: ''
      }
    };
  @StencilEvent({ bubbles: false, composed: false }) selectCard: EventEmitter<void>;
  @StencilEvent() loginSuccess: EventEmitter<void>;
  @StencilEvent() hydrated: EventEmitter<void>;
  @StencilEvent() addCardSuccess: EventEmitter<void>;
  @StencilEvent() logout: EventEmitter<void>;
  @StencilEvent() setSession: EventEmitter<void>;
  @StencilEvent() proceedToCart: EventEmitter<void>;
  @StencilEvent() proceedToWishlist: EventEmitter<void>;
  @StencilEvent() proceedToSearch: EventEmitter<void>;
  @StencilEvent() backClick: EventEmitter<void>;
  @State() showRedirectPopup: boolean = false;
  @State() redirectPopupContentType: string;
  @State() redirectTargetUrl: string = '';
  @State() openRedirectUrlInNewTab: string = '';
  @State() mainMenu: string = '';
  @State() title: string = '';
  @State() hasScrolled: boolean = false;
  @State() showLogin: boolean = false;
  @State() private loggedIn: boolean = this.isLoggedin;
  @State() showTicker: boolean = false;
  @State() tickerText: string = '';
  @State() customerInfoSession: any = {};
  @State() showAddCardPopup: boolean = false;
  @State() loginInfo: any = {};
  @State() addCardTitle: string = '';
  @State() disclaimerContent: Disclaimer;
  @State() isLoginRequired: boolean = false;
  @State() isAddCardRequired: boolean = false;
  apiService = new CommonApiService();
  emitAddCardToParent: boolean = false;
  emitLoginToParent: boolean = true;
  @Prop() tickerResponse: any = [];
  @Prop() disclaimerData: PartnerDisclaimerData;
  @Watch('disclaimerData')
  disclaimerDataChange() {
    const now = new Date();
    const response = this.parseTickerResponse(this.tickerResponse);
    const activeMessages = (Array.isArray(response) ? response : []).filter(msg => {
      const start = msg.startTime ? new Date(msg.startTime) : null;
      const end = msg.endTime ? new Date(msg.endTime) : null;
      return msg.isActive && msg.content?.trim() && (!start || now >= start) && (!end || now <= end);
    });
    this.showTicker = activeMessages.length > 0;
    this.tickerText = activeMessages.map((msg: any) => msg.content?.trim()).join('\u00A0\u00A0\u00A0• ');

    const parsedDisclaimerData = typeof this.disclaimerData === 'string' ? JSON.parse(atob(this.disclaimerData)) : this.disclaimerData;
    if (parsedDisclaimerData) {
      disclaimerStore.disclaimerData = parsedDisclaimerData;
    }
  }
  private callbackFn: ((details: loginResponse) => void) | null = null;
  private parseTickerResponse(raw: any): any[] {
    if (typeof raw === 'string') {
      try {
        return JSON.parse(atob(raw));
      } catch (err) {
        console.error('Failed to parse tickerMsgContents', err);
        return [];
      }
    }
    return raw || [];
  }

  private getCardHomePath(): string {
    const { mainDomain, subDomain } = this.domainUrl || {};
    const path = PathMap[this.activeProgram] ? '/' + PathMap[this.activeProgram] : '';
    if (this.isMain || !this.isLoggedin) {
      let { url = '', basePath = '' } = mainDomain || {};
      if (!url) url = new URL(window.location.href).origin;
      return new URL(this.loggedIn || path ? `${basePath}${path}/home` : `${basePath}/foryou`, url).toString();
    } else {
      let { url = '', basePath = '' } = subDomain || {};
      if (!url) url = new URL(window.location.href).origin;
      return new URL(this.loggedIn || path ? `${basePath}${path}/home` : `${basePath}/foryou`, url).toString();
    }
  }
  private closeModule() {
    const { subDomain } = this.domainUrl || {};
    let { url = '', basePath = '' } = subDomain || {};
    if (!url) url = new URL(window.location.href).origin;
    const closeUrl = new URL(this.loggedIn ? `${basePath}/home` : `${basePath}/foryou`, url).toString();
    window.location.replace(closeUrl);
  }


  componentWillLoad() {
    this.customerInfoSession = { ...this.customerInfo };
    if (typeof this.menuItems === 'string') {
      const decoded = atob(this.menuItems);
      this.menuItems = JSON.parse(decoded);
    }

    if (typeof this.domainUrl === 'string') {
      const decoded = atob(this.domainUrl);
      this.domainUrl = JSON.parse(decoded);
    }

    this.disclaimerDataChange();
    this.loggedIn = this.isLoggedin;
    try {
      if (!this.redirectDomainUrl) {
        this.redirectDomainUrl = new URL(window.location.href).origin;
      }
    } catch (e) {
      //Do nothing
    }
    pathStore.assetPath = this.assetPath;
    sharedStore.isMain = this.isMain;
  }

  private handleLoginEvent = (event: CustomEvent) => {
    const { notifyParent = false, callback, isLoginRequired = false } = event.detail || {};
    this.emitLoginToParent = notifyParent;
    this.callbackFn = callback;
    this.isLoginRequired = isLoginRequired;
    this.showLogin = true;
  };

  private checkloginEvent = (event: CustomEvent) => {
    const { internalCallback } = event.detail || {};
    if (this.customerInfoSession.customer_id) {
      internalCallback({ isLoggedin: true, ...this.customerInfoSession });
    } else {
      internalCallback({ isLoggedin: false });
    }
  };

  handleScroll = () => {
    if (typeof window === 'undefined') return;

    const scrollY = window.scrollY;
    const isScrolled = scrollY > 5;

    // Update internal state
    if (isScrolled !== this.hasScrolled) {
      this.hasScrolled = isScrolled;
    }

    // Toggle DOM class on <nav> directly
    const navEl = this.host.shadowRoot?.querySelector('nav');
    if (navEl) {
      navEl.classList.toggle('scrolled', isScrolled);
    }
  };

  private handleAddCardEvent = (event: CustomEvent) => {
    const { callback, notifyParent = false, addCardTitle = '', loginInfo = {}, isAddCardRequired = false } = event.detail || {};
    this.callbackFn = callback;
    this.emitAddCardToParent = notifyParent;
    this.loginInfo = loginInfo;
    this.showAddCardPopup = true;
    this.isAddCardRequired = isAddCardRequired;
    this.addCardTitle = addCardTitle;
  };

  private handleNoPathMatched = () => {
    // if (this.backButtonEnabled && window && window?.innerWidth < 768) this.showBackButton = true;
  };

  connectedCallback() {
    if (typeof window !== 'undefined') {
      window.addEventListener(this.loginEventName, this.handleLoginEvent);
      window.addEventListener(this.checkLoginEventName, this.checkloginEvent);
      window.addEventListener(this.addCardEventName, this.handleAddCardEvent);
      window.addEventListener('no-path-matched', this.handleNoPathMatched);
    }
  }

  disconnectedCallback() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener(this.loginEventName, this.handleLoginEvent);
      window.removeEventListener(this.addCardEventName, this.handleAddCardEvent);
      window.removeEventListener('no-path-matched', this.handleNoPathMatched);
      window.removeEventListener(this.checkLoginEventName, this.checkloginEvent);
    }
  }

  private getCardDisplayValue(): string | JSX.Element {
    const cardTypeToUse = this.selectedCard?.cardType || this.activeProgram;
    const normalizedCardType = cardTypeToUse?.trim().toLowerCase();
    const isValidCardType = typeof normalizedCardType === 'string' && this.allowedCardTypes.includes(normalizedCardType);
    if (isValidCardType && cardTypeLogos[normalizedCardType]) {
      return <img src={`${this.assetPath}images/header/card-logos/${cardTypeLogos[normalizedCardType]}`} alt={normalizedCardType} class=" object-contain" />;
    }
    // Fallback to uppercase formatted text
    return <span class="font-playfair text-xl tracking-[4px] uppercase">{cardTypeToUse?.replace(/[_-]/g, ' ').toUpperCase()}</span>;
  }

  componentDidLoad() {
    this.hydrated.emit();
    if (typeof window !== 'undefined') {
      this.handleScroll();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      const match = document.cookie.match(new RegExp('(^| )' + ConfigService.cookieConsentName + '=([^;]+)'));
      const consentCookie = match ? decodeURIComponent(match[2]) : null;
      if (Number(consentCookie)) {
        this.appendGoogleAnalytics({ detail: { value: consentCookie } })
      }
    }
  }

  private appendGoogleAnalytics(event?: any) {
    try {
      const { detail } = event || {};
      if (!ConfigService.googleAnalyticsKey) {
        console.log("Anayalytics key not found");
        return;
      }
      if (detail?.value) {
        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${ConfigService.googleAnalyticsKey}`;
        document.head.appendChild(gtagScript);

        // Add the inline gtag config script
        const inlineScript = document.createElement('script');
        inlineScript.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${ConfigService.googleAnalyticsKey}');
        `;
        document.head.appendChild(inlineScript);
      } else {
        console.log("Not tracking");
      }
    } catch (error) {
      console.log("error setting Google Analytics script:", error);
    }
  }

  showRedirectionPopup = (event: CustomEvent) => {
    const { redirectPopupContentType, redirectTargetUrl, disclaimerContent, openRedirectUrlInNewTab, mainMenu, title } = event.detail || {};
    this.showRedirectPopup = true;
    this.redirectPopupContentType = redirectPopupContentType;
    this.redirectTargetUrl = redirectTargetUrl;
    this.disclaimerContent = disclaimerContent;
    this.openRedirectUrlInNewTab = openRedirectUrlInNewTab;
    this.mainMenu = mainMenu;
    this.title = title;
  };

  handleMoengageEvent = (button_name: string) => {
    const uniqueId = window.crypto.randomUUID();
    const moengageAttributes = {
      "redirection_url": this.redirectTargetUrl,
      "section": "disclaimer-popup",
      "source": `header`,
      "category": this.mainMenu,
      "sub_category": this.title,
      "event_unique_id": `${uniqueId}_${this.title.toLowerCase()}`,
      "button_name": button_name
    }
    console.log("MoEngage Event Attributes:", moengageAttributes);
    MoengageInstance.trackEvent("disclaimer", moengageAttributes);
  }

  handleRedirectProceed = () => {
    this.showRedirectPopup = false;
    this.handleMoengageEvent('Proceed');
    if (this.openRedirectUrlInNewTab) {
      window.open(this.redirectTargetUrl);
    } else if (this.redirectTargetUrl) {
      setTimeout(() => {
        window.location.href = this.redirectTargetUrl;
      }, 500);
    }
  };

  handleRedirectClose = () => {
    this.showRedirectPopup = false;
    this.handleMoengageEvent('Close');
  };

  handleCardSelected = async (event: any) => {
    event.stopPropagation();
    this.selectCard.emit(event.detail);
  };

  onLoginSuccess = (event: any) => {
    if (this.emitLoginToParent) {
      this.loginSuccess.emit(event.detail);
      this.showLogin = false;
      loginController.showLoader({
        title: 'Authenticating...'
      });
    } else if (this.callbackFn) {
      this.callbackFn({
        info: { ...event.detail, authenticated: true }
      });
    }
  };

  onLoginCancel = (event: any) => {
    if (event.detail?.skipDetails && this.emitLoginToParent) {
      this.loginSuccess.emit(event.detail);
    } else if (event.detail?.skipDetails) {
      if (this.callbackFn) {
        this.callbackFn({ info: { ...event.detail, authenticated: true } });
      }
    } else if (this.callbackFn) {
      this.callbackFn({ info: { authenticated: false } });
    }

    this.showLogin = false;
  };

  onLogOut = (event: any) => {
    event.stopPropagation();
    this.logout.emit();
  };

  onCloseAddCard = (event: any) => {
    const { detail } = event || {};
    this.showAddCardPopup = false;
    if (this.emitAddCardToParent) {
      let cardType = detail.cardType;
      cardType = cardType[0].toUpperCase() + cardType?.slice(1)?.toLowerCase();
      const cardName = cardType.replaceAll('_', ' ');
      loginController.showLoader({
        title: 'Taking you to ' + cardName + ' experience...',
        cardFaciaUrl: detail.imageUrl || ''
      });
      this.addCardSuccess.emit(detail);
    } else if (this.callbackFn) {
      this.callbackFn({ info: { ...detail, authenticated: true } });
    }
  };

  onCancelAddCard = () => {
    this.showAddCardPopup = false;
    if (this.callbackFn) {
      this.callbackFn({ info: { authenticated: false } });
    }
  };

  onResetSession = (event: any) => {
    this.setSession.emit(event.detail);
  };

  render() {
    const bgStyle = {
      position: this.position,
      zIndex: '110',
      top: '0'
    };

    const bgClass = this.headerTransparent ? (this.hasScrolled ? 'bg-header scrolled' : 'transparent') : 'bg-header';

    const navClasses = `${bgClass} transition-colors duration-300 ease-in-out ${this.visibleOnMobile ? 'flex' : 'hidden'
      } md:flex flex-col px-4 py-3 md:px-0 md:py-4 w-full ${this.isLoggedin ? 'gap-4' : 'gap-1'} text-white`;

    return (
      <Host style={bgStyle}>
        <div class="flex flex-col-reverse md:flex-col">
          <If
            condition={this.showTicker}
            children={
              <div class={`${this.isHomePage ? 'flex' : 'hidden md:flex'} flex-col sm:flex-row items-center justify-center h-12 sm:h-8 bg-pink-50 px-3`}>
                <div class="text-xs font-medium text-pink-500 uppercase px-0 sm:px-3 w-full sm:size-fit shrink-0">Important Message:</div>
                <div class="relative overflow-hidden w-full h-5">
                  <div class="scroll-marquee text-gray-700 font-bold text-sm flex items-center" innerHTML={this.tickerText}></div>
                </div>
              </div>
            }
          />
          <If
            condition={this.displayHeader}
            children={
              <nav class={navClasses}>
                <div class="flex justify-between md:px-15 items-center">
                  <span class={`md:hidden w-6 ${this.isMain && !this.showBackButton ? 'block' : 'hidden'}`}></span>
                  {this.isMain ? (
                    <div class="flex max-w-1/2 items-center gap-4">
                      <div class={`${!this.showBackButton ? 'hidden' : 'block md:hidden'}`} onClick={() => this.backClick.emit()}>
                        <img width={24} height={24} alt="smartbuy" src={`${this.assetPath}icons/back-icon.svg`} />
                      </div>
                      <a href={this.bankDomainUrl}>
                        <img width={24} height={24} alt="smartbuy" src={`${this.assetPath}images/hdfc-main.svg`} />
                      </a>
                      <a href={this.getCardHomePath()} title="SmartBuy" class={`${this.showBackButton ? 'hidden md:flex' : 'flex'} items-center gap-4`}>
                        <img alt="smartbuy" src={`${this.assetPath}images/Smartbuylogo.png`} />
                        <If
                          condition={
                            !!(this.selectedCard?.cardType || this.activeProgram) &&
                            this.allowedCardTypes.includes((this.selectedCard?.cardType || this.activeProgram).trim().toLowerCase())
                          }
                          children={
                            <div class="hidden md:flex items-center gap-4">
                              <span class="hidden md:block">|</span>
                              {this.getCardDisplayValue()}
                            </div>
                          }
                        />
                      </a>
                    </div>
                  ) : (
                    <div>
                      <div class={`${this.isHomePage || !this.showBackButton ? 'hidden' : 'block md:hidden'}`} onClick={() => this.backClick.emit()}>
                        <img width={24} height={24} alt="smartbuy" src={`${this.assetPath}icons/back-icon.svg`} />
                      </div>
                      <a href={this.getCardHomePath()} title="Home" class={`${this.showBackButton ? 'hidden md:flex' : ''}  items-center`}>
                        <img width={24} height={24} alt="smartbuy" src={`${this.assetPath}images/home_logo.png`} />
                      </a>
                    </div>
                  )}
                  <quick-access-bar
                    isLoggedin={this.isLoggedin}
                    redirectDomainUrl={this.redirectDomainUrl}
                    showSearchIcon={this.showSearchIcon}
                    wishList={this.wishList}
                    cartFlow={this.cartFlow}
                    customLogout={this.customLogout}
                    customerInfo={this.customerInfo}
                    firstName={this.firstName}
                    onShowLogin={() => (this.showLogin = true)}
                    onLogout={this.onLogOut}
                    onProceedToSearch={() => this.proceedToSearch.emit()}
                    onProceedToCart={() => this.proceedToCart.emit()}
                    showClose={this.showBackButton}
                    onProceedToWishlist={() => this.proceedToWishlist.emit()}
                    onCloseModule={() => this.closeModule()}
                    isMain={this.isMain}
                  />
                </div>
                <div class="hidden md:flex items-stretch justify-between gap-3 md:ml-10 md:mr-15">
                  <menu-list
                    ssrEnabled={this.ssrEnabled}
                    class="flex-grow min-w-0"
                    onShowRedirectPopup={this.showRedirectionPopup}
                    menuItems={this.menuItems}
                    activeMenuItem={this.activeMenuItem}
                  />
                  <If
                    condition={this.loggedIn && this.showCardSwitcher}
                    children={
                      <card-details
                        class="self-center flex-shrink-0"
                        switchEnabled={this.switchEnabled}
                        customerInfo={this.customerInfo}
                        scrolled={this.hasScrolled}
                        headerTransparent={this.headerTransparent}
                        onSelectCard={this.handleCardSelected}
                        assetPath={this.assetPath}
                        cardsAvailable={this.cardsAvailable}
                        selectedCard={this.selectedCard}
                        activeProgram={this.activeProgram}
                        selectedCardName={this.selectedCardName}
                        selectedCardPoints={this.selectedCardPoints}
                        selectedCardLastFourDigits={this.selectedCardLastFourDigits}
                      />
                    }
                  />
                </div>
              </nav>
            }
          />
        </div>

        <If condition={this.showLogin} children={<auth-flow isLoginRequired={this.isLoginRequired} onSuccess={this.onLoginSuccess} onCancel={this.onLoginCancel} />} />

        <If
          condition={this.showRedirectPopup}
          children={
            <redirect-popup
              open={this.showRedirectPopup}
              contentType={this.redirectPopupContentType}
              onConfirm={this.handleRedirectProceed}
              onClose={this.handleRedirectClose}
              redirectTargetUrl={this.redirectTargetUrl}
              disclaimerContent={this.disclaimerContent}
            />
          }
        />
        <If condition={this.enableSessionHandler} children={<idle-timer onSetSession={this.onResetSession} />} />
        <If
          condition={this.enableCookieConsent}
          children={
            <general-consent
              activeProgram={this.activeProgram}
              redirectDomainUrl={this.redirectDomainUrl}
              allowedCardTypes={this.allowedCardTypes}
              selectedCard={this.selectedCard}
              onCookieConsentChange={this.appendGoogleAnalytics}
            />
          }
        />
        <If condition={this.platformPopupEnabled} children={<platform-popup activeProgram={this.activeProgram} />} />
        <If
          condition={this.showAddCardPopup && window !== undefined}
          children={
            <add-card
              loginInfo={this.loginInfo}
              addCardTitle={this.addCardTitle}
              customerInfo={this.customerInfo}
              onCloseAddCard={this.onCloseAddCard}
              onCancelAddCard={this.onCancelAddCard}
              assetPath={this.assetPath}
              isAddCardRequired={this.isAddCardRequired}
            />
          }
        />
        <blur-loader />
      </Host>
    )
  }
}
